import {validateCompanyId, validateNationalId} from './israeli-id';
import {validatePassportNumber} from './passport';

import type {CompanyIdError, NationalIdError} from './israeli-id';
import type {PassportError} from './passport';

export type IdentityValidationError = NationalIdError | CompanyIdError | PassportError;

/**
 * Picks which identity validator applies — Israeli company ID for a corporation, foreign
 * passport for an individual who selected one, Israeli national ID otherwise — so the choice
 * lives in one place instead of being re-derived independently by each caller (backend and
 * frontend schemas both need it).
 */
export function resolveIdentityValidationError(params: {
  isCorporate: boolean;
  isPassport: boolean;
  value: string;
}): IdentityValidationError | null {
  if (params.isCorporate) return validateCompanyId(params.value);
  if (params.isPassport) return validatePassportNumber(params.value);
  return validateNationalId(params.value);
}

/**
 * Comparison key for "these two parties are the same entity". Null for a blank number — a draft row
 * identifies nobody. `isPassport` (derived as for `resolveIdentityValidationError`) scopes the key by
 * issuing country, since only passport numbers are namespaced per country.
 */
export function buildIdentityKey(params: {
  idNumber: string | null | undefined;
  isPassport: boolean;
  idCountry: string | null | undefined;
}): string | null {
  const number = normalizeIdentityNumber(params.idNumber);
  if (!number) return null;

  return `${number}|${params.isPassport ? normalizeIdentityNumber(params.idCountry) : ''}`;
}

/** Strips the separators people type inconsistently (`51-234 567.8`) so they compare equal. */
export function normalizeIdentityNumber(value: string | null | undefined): string {
  return (value ?? '').replace(/[\s\-._/]/g, '').toUpperCase();
}
